import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import {
  Upload, Loader2, AlertCircle, CheckCircle2, FileImage, RefreshCw, Camera, Car, User, Building2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VEHICULE_FIELDS = [
  { key: 'marque', label: 'Marque' },
  { key: 'modele', label: 'Modèle' },
  { key: 'immatriculation', label: 'Numéro de plaques' },
  { key: 'vin', label: 'Numéro de châssis (VIN)' },
  { key: 'cylindree_cm3', label: 'Cylindrée (cm³)' },
  // puissance affichée à part (kW + CV)
  { key: 'date_premiere_mise_en_circulation', label: '1re mise en circulation' },
];

const colorClasses = {
  green: 'border-green-300 bg-green-50/40 focus-visible:ring-green-500',
  orange: 'border-orange-400 bg-orange-50 focus-visible:ring-orange-500',
  red: 'border-red-400 bg-red-50 focus-visible:ring-red-500',
};
const dotColor = { green: 'bg-green-500', orange: 'bg-orange-500', red: 'bg-red-500' };

const CarteGriseScanner = ({ open, onClose, onConfirm }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [step, setStep] = useState('upload'); // upload | analyzing | review
  const [error, setError] = useState('');
  const [data, setData] = useState({});
  const [confidence, setConfidence] = useState({});
  const [warnings, setWarnings] = useState([]);
  const [legacy, setLegacy] = useState(null);

  const reset = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setFile(null); setPreviewUrl(''); setStep('upload');
    setError(''); setData({}); setConfidence({}); setWarnings([]); setLegacy(null);
  };

  const handleFile = (f) => {
    setError('');
    if (!f) return;
    if (!f.type.startsWith('image/')) { setError('Veuillez sélectionner une image JPEG ou PNG.'); return; }
    if (f.size > 10 * 1024 * 1024) { setError('Image trop volumineuse (max 10 Mo).'); return; }
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const handleScan = async () => {
    if (!file) return;
    setError(''); setWarnings([]); setStep('analyzing');

    const formData = new FormData();
    formData.append('file', file);

    try {
      // XHR (not fetch) — PostHog can wrap fetch and break the response body
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${API}/ocr/carte-grise`);
      const response = await new Promise((resolve, reject) => {
        xhr.onload = () => {
          try { resolve({ status: xhr.status, body: JSON.parse(xhr.responseText) }); }
          catch { reject(new Error('Réponse invalide')); }
        };
        xhr.onerror = () => reject(new Error('Erreur réseau'));
        xhr.send(formData);
      });

      if (response.status >= 400) {
        throw new Error(response.body.detail || "Erreur lors de l'analyse");
      }
      setData(response.body.data || {});
      setConfidence(response.body.confidence || {});
      setWarnings(response.body.warnings || []);
      setLegacy(response.body.legacy || null);
      setStep('review');
    } catch (err) {
      setError(err.message);
      setStep('upload');
    }
  };

  const updateField = (key, value) => {
    setData((prev) => ({ ...prev, [key]: value }));
    setConfidence((prev) => ({
      ...prev,
      [key]: value === '' || value === null || value === undefined
        ? 'red'
        : prev[key] === 'red' ? 'green' : (prev[key] || 'green'),
    }));
  };

  const setType = (newType) => {
    setData((prev) => ({ ...prev, type_detenteur: newType }));
  };

  const handleConfirm = () => {
    const edited = { ...data };
    // Re-derive legacy from possibly-edited data
    const updatedLegacy = legacy ? { ...legacy } : {};
    if (edited.marque) updatedLegacy.brand = edited.marque;
    if (edited.modele) updatedLegacy.model = edited.modele;
    if (edited.vin) updatedLegacy.vin = edited.vin;
    if (edited.immatriculation) updatedLegacy.plate_number = edited.immatriculation;
    if (edited.date_premiere_mise_en_circulation) {
      const m = String(edited.date_premiere_mise_en_circulation).match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (m) {
        updatedLegacy.year = parseInt(m[3], 10);
        updatedLegacy.first_registration_date = `${m[3]}-${m[2]}-${m[1]}`;
      }
    }
    if (edited.cylindree_cm3) {
      const v = parseInt(String(edited.cylindree_cm3).replace(/\s/g, ''), 10);
      if (!isNaN(v)) updatedLegacy.displacement_cc = v;
    }
    if (edited.puissance_kw) {
      const kw = parseFloat(String(edited.puissance_kw).replace(/\s/g, '').replace(',', '.'));
      if (!isNaN(kw)) {
        updatedLegacy.power_kw = kw;
        updatedLegacy.power_ch = Math.round(kw * 1.35962);
      }
    }
    updatedLegacy.detention = {
      type: edited.type_detenteur || 'particulier',
      first_name: edited.detention_prenom,
      last_name: edited.detention_nom,
      company_name: edited.detention_raison_sociale,
      address: edited.detention_adresse,
      zip_code: edited.detention_npa,
      city: edited.detention_ville,
      is_company: edited.type_detenteur === 'entreprise',
    };

    onConfirm({ data: edited, legacy: updatedLegacy });
    reset();
  };

  const handleClose = () => { reset(); onClose(); };

  const isEntreprise = data.type_detenteur === 'entreprise';
  const cv = data.puissance_kw ? Math.round(parseFloat(String(data.puissance_kw).replace(',', '.')) * 1.35962) : '';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="carte-grise-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileImage className="w-5 h-5 text-[#52a675]" />
            Scanner ma carte grise
          </DialogTitle>
          <DialogDescription>
            {step === 'upload' && "Prenez ou téléchargez une photo de votre carte grise suisse."}
            {step === 'analyzing' && "Analyse en cours par l'IA…"}
            {step === 'review' && "Vérifiez les informations extraites avant de continuer."}
          </DialogDescription>
        </DialogHeader>

        {/* STEP: UPLOAD */}
        {step === 'upload' && (
          <div className="space-y-4">
            {error && (
              <Alert variant="destructive" data-testid="cg-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div
              className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-[#52a675] hover:bg-[#52a675]/5 transition"
              onClick={() => fileRef.current?.click()}
              data-testid="cg-drop-zone"
            >
              {previewUrl ? (
                <img src={previewUrl} alt="preview" className="max-h-72 mx-auto rounded-lg shadow" />
              ) : (
                <>
                  <Upload className="w-10 h-10 mx-auto text-gray-400 mb-2" />
                  <p className="text-sm text-gray-700 font-medium">Cliquez pour choisir une image</p>
                  <p className="text-xs text-gray-500 mt-1">JPEG ou PNG, jusqu'à 10 Mo</p>
                </>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="cg-file-input"
              />
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-3 rounded text-xs text-blue-800">
              <strong>Conseils :</strong> photo prise à plat, bien éclairée, sans reflet, cadrant <em>l'ensemble</em> de la carte grise.
            </div>

            <div className="flex justify-between gap-2">
              <Button variant="outline" onClick={handleClose} data-testid="cg-cancel">Annuler</Button>
              <div className="flex gap-2">
                {previewUrl && (
                  <Button variant="outline" onClick={() => { setFile(null); setPreviewUrl(''); }} data-testid="cg-clear">
                    Changer
                  </Button>
                )}
                <Button onClick={handleScan} disabled={!file} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="cg-scan-button">
                  <Camera className="w-4 h-4 mr-2" /> Analyser
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* STEP: ANALYZING */}
        {step === 'analyzing' && (
          <div className="py-16 text-center">
            <Loader2 className="w-10 h-10 mx-auto animate-spin text-[#52a675] mb-3" />
            <p className="text-gray-600">L'IA analyse votre carte grise...</p>
            <p className="text-xs text-gray-400 mt-1">Cela peut prendre quelques secondes.</p>
          </div>
        )}

        {/* STEP: REVIEW */}
        {step === 'review' && (
          <div className="space-y-5" data-testid="cg-review">
            {/* Big orange banner */}
            <div
              className="border-2 border-orange-400 bg-orange-50 rounded-lg p-3 flex items-start gap-2"
              data-testid="cg-orange-banner"
            >
              <AlertCircle className="w-5 h-5 text-orange-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm font-semibold text-orange-900">
                Veuillez vérifier et corriger les informations si nécessaire avant de continuer
              </p>
            </div>

            {warnings.length > 0 && (
              <Alert className="bg-orange-50 border-orange-300">
                <AlertCircle className="h-4 w-4 text-orange-600" />
                <AlertDescription className="text-orange-900 text-sm">
                  <ul className="list-disc list-inside">
                    {warnings.map((w, i) => <li key={i}>{w}</li>)}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> Valeur OK</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-orange-500" /> À vérifier</span>
              <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> À remplir</span>
            </div>

            {/* SECTION 1: VÉHICULE */}
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Car className="w-4 h-4 text-[#52a675]" /> Véhicule
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {VEHICULE_FIELDS.map((f) => {
                  const c = confidence[f.key] || 'red';
                  return (
                    <div key={f.key}>
                      <Label className="text-xs flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${dotColor[c]}`} />
                        {f.label}
                      </Label>
                      <Input
                        value={data[f.key] ?? ''}
                        onChange={(e) => updateField(f.key, e.target.value)}
                        placeholder={c === 'red' ? 'À remplir manuellement' : ''}
                        className={`mt-1 ${colorClasses[c] || colorClasses.red}`}
                        data-testid={`cg-field-${f.key}`}
                      />
                    </div>
                  );
                })}

                {/* Puissance kW + CV (read-only CV) */}
                <div className="md:col-span-2 grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${dotColor[confidence.puissance_kw || 'red']}`} />
                      Puissance (kW)
                    </Label>
                    <Input
                      value={data.puissance_kw ?? ''}
                      onChange={(e) => updateField('puissance_kw', e.target.value)}
                      placeholder={confidence.puissance_kw === 'red' ? 'À remplir' : ''}
                      className={`mt-1 ${colorClasses[confidence.puissance_kw] || colorClasses.red}`}
                      data-testid="cg-field-puissance_kw"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-600">Puissance (CV) — calculée</Label>
                    <Input
                      value={cv}
                      readOnly
                      tabIndex={-1}
                      className="mt-1 bg-gray-100 cursor-not-allowed"
                      data-testid="cg-field-puissance_ch"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* SECTION 2: DÉTENTEUR */}
            <section>
              <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
                {isEntreprise ? <Building2 className="w-4 h-4 text-[#52a675]" /> : <User className="w-4 h-4 text-[#52a675]" />}
                Détenteur
              </h3>

              {/* Type toggle */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                <button
                  type="button"
                  onClick={() => setType('particulier')}
                  className={`p-2 rounded-lg border text-sm font-medium transition ${
                    !isEntreprise ? 'border-[#52a675] bg-[#52a675]/10 text-[#3d8a5c]' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid="cg-type-particulier"
                >
                  <User className="w-4 h-4 inline mr-1" /> Particulier
                </button>
                <button
                  type="button"
                  onClick={() => setType('entreprise')}
                  className={`p-2 rounded-lg border text-sm font-medium transition ${
                    isEntreprise ? 'border-[#52a675] bg-[#52a675]/10 text-[#3d8a5c]' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                  }`}
                  data-testid="cg-type-entreprise"
                >
                  <Building2 className="w-4 h-4 inline mr-1" /> Entreprise
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {isEntreprise ? (
                  <div className="md:col-span-2">
                    <Label className="text-xs flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${dotColor[confidence.detention_raison_sociale || 'red']}`} />
                      Raison sociale
                    </Label>
                    <Input
                      value={data.detention_raison_sociale ?? ''}
                      onChange={(e) => updateField('detention_raison_sociale', e.target.value)}
                      placeholder={confidence.detention_raison_sociale === 'red' ? 'À remplir manuellement' : ''}
                      className={`mt-1 ${colorClasses[confidence.detention_raison_sociale] || colorClasses.red}`}
                      data-testid="cg-field-detention_raison_sociale"
                    />
                  </div>
                ) : (
                  <>
                    <div>
                      <Label className="text-xs flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${dotColor[confidence.detention_nom || 'red']}`} />
                        Nom
                      </Label>
                      <Input
                        value={data.detention_nom ?? ''}
                        onChange={(e) => updateField('detention_nom', e.target.value)}
                        placeholder={confidence.detention_nom === 'red' ? 'À remplir' : ''}
                        className={`mt-1 ${colorClasses[confidence.detention_nom] || colorClasses.red}`}
                        data-testid="cg-field-detention_nom"
                      />
                    </div>
                    <div>
                      <Label className="text-xs flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${dotColor[confidence.detention_prenom || 'red']}`} />
                        Prénom
                      </Label>
                      <Input
                        value={data.detention_prenom ?? ''}
                        onChange={(e) => updateField('detention_prenom', e.target.value)}
                        placeholder={confidence.detention_prenom === 'red' ? 'À remplir' : ''}
                        className={`mt-1 ${colorClasses[confidence.detention_prenom] || colorClasses.red}`}
                        data-testid="cg-field-detention_prenom"
                      />
                    </div>
                  </>
                )}

                <div className="md:col-span-2">
                  <Label className="text-xs flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${dotColor[confidence.detention_adresse || 'red']}`} />
                    Adresse
                  </Label>
                  <Input
                    value={data.detention_adresse ?? ''}
                    onChange={(e) => updateField('detention_adresse', e.target.value)}
                    placeholder={confidence.detention_adresse === 'red' ? 'Rue et numéro' : ''}
                    className={`mt-1 ${colorClasses[confidence.detention_adresse] || colorClasses.red}`}
                    data-testid="cg-field-detention_adresse"
                  />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${dotColor[confidence.detention_npa || 'red']}`} />
                    Code postal (NPA)
                  </Label>
                  <Input
                    value={data.detention_npa ?? ''}
                    onChange={(e) => updateField('detention_npa', e.target.value)}
                    placeholder={confidence.detention_npa === 'red' ? '4 chiffres' : ''}
                    className={`mt-1 ${colorClasses[confidence.detention_npa] || colorClasses.red}`}
                    data-testid="cg-field-detention_npa"
                  />
                </div>
                <div>
                  <Label className="text-xs flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${dotColor[confidence.detention_ville || 'red']}`} />
                    Ville
                  </Label>
                  <Input
                    value={data.detention_ville ?? ''}
                    onChange={(e) => updateField('detention_ville', e.target.value)}
                    placeholder={confidence.detention_ville === 'red' ? 'À remplir' : ''}
                    className={`mt-1 ${colorClasses[confidence.detention_ville] || colorClasses.red}`}
                    data-testid="cg-field-detention_ville"
                  />
                </div>
              </div>
            </section>

            <DialogFooter className="gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep('upload')} data-testid="cg-redo">
                <RefreshCw className="w-4 h-4 mr-2" /> Reprendre
              </Button>
              <Button onClick={handleConfirm} className="bg-[#52a675] hover:bg-[#3d8a5c]" data-testid="cg-confirm-button">
                <CheckCircle2 className="w-4 h-4 mr-2" /> Confirmer et continuer
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CarteGriseScanner;
